// frontend/app/components/DavinciChallengeIntro.js
//
// BDD 2026-06-18 — Da Vinci "Science Exploration Trail" hub.
//
// Full-screen branded landing for an enrolled Da Vinci student: marketing copy
// (EN/ES) above a grid of Collection tiles (one per challenge day). Tiles open
// their collection (current/completed are tappable; future days are locked). A
// gold "Get Your Exclusive QR Code" button enables only once every Collection
// has been explored, and triggers the certificate flow.
//
// Web/kiosk-first, RN primitives so it renders anywhere. Navy/gold theme.

import React, { useEffect } from "react";
import { View, Text, Image, TouchableOpacity, Pressable, ScrollView, StyleSheet, Platform } from "react-native";

const DAVINCI_LOGO = require("../../assets/images/davinci.png");
const SARAS_LOGO = require("../../assets/Saras-3D_Logo.png");

const ROOT_ID = "dv-intro-root";
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Jost:wght@400;500;600&display=swap";

const injectIntroTheme = () => {
  if (typeof document === "undefined") return;
  if (!document.getElementById("dv-intro-fonts")) {
    const l = document.createElement("link");
    l.id = "dv-intro-fonts";
    l.rel = "stylesheet";
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }
  if (document.getElementById("dv-intro-style")) return;
  const s = document.createElement("style");
  s.id = "dv-intro-style";
  s.textContent = `
    #${ROOT_ID} {
      background:
        radial-gradient(60% 40% at 50% -10%, #18367a 0%, rgba(24,54,122,0) 60%),
        radial-gradient(70% 55% at 50% 120%, #142a63 0%, rgba(20,42,99,0) 55%),
        linear-gradient(160deg,#060f2c 0%,#0b1f4d 50%,#060f2c 100%) !important;
    }
  `;
  document.head.appendChild(s);
};

const FONT_DISPLAY = '"Cormorant Garamond", Georgia, serif';
const FONT_LABEL = '"Jost", system-ui, sans-serif';
const GOLD = "#e7c46a";

// Bilingual marketing copy. {n} -> number of days/tiles.
const COPY = {
  en: {
    partner: "Da Vinci Science Center  ×  Saras-3D",
    header: "Welcome to the Science Exploration Trail",
    tagline: "Explore, learn, and get certified!",
    intro:
      "Dive deeper into the exciting topics you interacted with at the Da Vinci Science Center exhibit.",
    bullets: [
      "Watch videos, read summaries and interact with 3D content within each Collection below.",
      "Explore each Collection's content before moving on to the next.",
      "Unlock a special QR code at the end of the Collections exploration trail.",
      "Visit Da Vinci Science Center with the QR code to receive your personalized certificate.",
    ],
    suffix: "Collection",
    locked: "Locked",
    done: "Done",
    qrButton: "Get Your Exclusive QR Code",
    close: "Close",
    footer:
      "Click each tile above to learn and Ask Saras AI about the topic. Generate QR code for your certificate after exploring all {n} tile contents.",
  },
  es: {
    partner: "Da Vinci Science Center  ×  Saras-3D",
    header: "Bienvenido al Sendero de Exploración Científica",
    tagline: "¡Explore, aprenda y obtenga su certificación!",
    intro:
      "Sumérgete más en los temas emocionantes con los que interactuaste en la exhibición del Da Vinci Science Center.",
    bullets: [
      "Mira videos, lee resúmenes e interactúa con contenido 3D dentro de cada Colección a continuación.",
      "Explora el contenido de cada colección antes de pasar a la siguiente.",
      "Desbloquea un código QR especial al final del recorrido de exploración de las Colecciones.",
      "Visite el Centro de Ciencias Da Vinci con el código QR para recibir su certificado personalizado.",
    ],
    suffix: "Colección",
    locked: "Bloqueado",
    done: "Completado",
    qrButton: "Consigue Su Código QR Exclusivo",
    close: "Cerrar",
    footer:
      "Haga clic en cada casilla arriba para aprender y preguntar a Saras AI sobre el tema. Genere un código QR para su certificado después de explorar el contenido de las {n} casillas.",
  },
};

export default function DavinciChallengeIntro({
  challengeState,
  language = "en",
  isDarkMode = true,
  onSelectCollection,
  onGetCertificate,
  onClose,
}) {
  useEffect(() => {
    if (Platform.OS === "web") injectIntroTheme();
  }, []);

  const L = (language || "en").toLowerCase().startsWith("es") ? COPY.es : COPY.en;
  const isEs = L === COPY.es;

  const days = Array.isArray(challengeState?.days) ? challengeState.days : [];
  const n = days.length;
  const thresholdPct =
    challengeState?.current?.advance_threshold_pct ??
    days[0]?.advance_threshold_pct ??
    80;
  const allDone = !!challengeState?.all_done || (n > 0 && days.every((d) => d?.completed_at));

  const tileTitle = (d) => {
    const name = (d?.subject_label || d?.collection_name || `${d?.day ?? ""}`).trim();
    return isEs ? `${L.suffix} ${name}` : `${name} ${L.suffix}`;
  };
  const dayStatus = (d) => {
    if (d?.completed_at) return "done";
    if (d?.unlocked) return "open";
    return "locked";
  };

  return (
    <View nativeID={ROOT_ID} style={styles.root}>
      {typeof onClose === "function" && (
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={L.close}
          hitSlop={8}
          style={({ pressed }) => [styles.closeBtn, pressed && styles.closeBtnPressed]}
        >
          <Text style={styles.closeBtnText}>×</Text>
        </Pressable>
      )}
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        {/* Logos + partner line */}
        <View style={styles.brand}>
          <Image source={DAVINCI_LOGO} style={styles.logoDv} resizeMode="contain" />
          <View style={styles.divider} />
          <Image source={SARAS_LOGO} style={styles.logoSaras} resizeMode="contain" />
        </View>
        <Text style={styles.partner}>{L.partner}</Text>

        {/* Header + tagline */}
        <Text style={styles.header}>{L.header}</Text>
        <Text style={styles.tagline}>{L.tagline}</Text>

        {/* Instructional copy */}
        <Text style={styles.intro}>{L.intro}</Text>
        <View style={styles.bullets}>
          {L.bullets.map((b, i) => (
            <View key={i} style={styles.bulletRow}>
              <Text style={styles.bulletDot}>•</Text>
              <Text style={styles.bulletText}>
                {b.replace("{x}", String(thresholdPct))}
              </Text>
            </View>
          ))}
        </View>

        {/* Collection tiles */}
        <View style={styles.tiles}>
          {days.map((d) => {
            const st = dayStatus(d);
            const tappable = st !== "locked";
            return (
              <TouchableOpacity
                key={d?.day ?? Math.random()}
                activeOpacity={tappable ? 0.85 : 1}
                disabled={!tappable}
                onPress={() => tappable && onSelectCollection && onSelectCollection(d.collection_id)}
                style={[
                  styles.tile,
                  st === "open" && styles.tileOpen,
                  st === "done" && styles.tileDone,
                  st === "locked" && styles.tileLocked,
                ]}
              >
                <Text style={styles.tileKicker}>
                  {st === "done" ? `✓ ${L.done}` : st === "locked" ? `🔒 ${L.locked}` : `${d?.percent ?? 0}%`}
                </Text>
                <Text style={styles.tileTitle} numberOfLines={2}>
                  {tileTitle(d)}
                </Text>
              </TouchableOpacity>
            );
          })}
          {n === 0 && (
            <Text style={styles.intro}>
              {isEs
                ? "Tu desafío se está configurando — consulta con el personal."
                : "Your challenge is being set up — please check with staff."}
            </Text>
          )}
        </View>

        {/* QR button — enabled only once every Collection is explored */}
        <TouchableOpacity
          style={[styles.qrBtn, !allDone && styles.qrBtnDisabled]}
          onPress={() => allDone && onGetCertificate && onGetCertificate()}
          disabled={!allDone}
          activeOpacity={0.85}
        >
          <Text style={styles.qrBtnText}>{L.qrButton}</Text>
        </TouchableOpacity>

        <Text style={styles.footer}>{L.footer.replace("{n}", String(n || 6))}</Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: "#070f2b",
  },
  scroll: {
    alignItems: "center",
    paddingVertical: 44,
    paddingHorizontal: 28,
    maxWidth: 940,
    width: "100%",
    alignSelf: "center",
  },
  brand: { flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: 8 },
  // Boxes sized so the VISIBLE artwork of each logo is ~50px tall (davinci.png is
  // 500x200 with 147px of art; Saras-3D_Logo.png is 1920x1080 with 748px of art).
  logoDv: { height: 68, width: 170 },
  logoSaras: { height: 72, width: 128 },
  divider: { width: 1, height: 34, backgroundColor: "rgba(231,196,106,0.45)", marginHorizontal: 18 },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    backgroundColor: "rgba(15,33,77,0.75)",
    borderWidth: 1,
    borderColor: "rgba(231,196,106,0.55)",
  },
  closeBtnPressed: { backgroundColor: "rgba(231,196,106,0.25)" },
  closeBtnText: { color: GOLD, fontSize: 28, lineHeight: 30, fontWeight: "600", textAlign: "center" },
  partner: { fontFamily: FONT_LABEL, fontSize: 12, letterSpacing: 3, fontWeight: "600", color: GOLD, textTransform: "uppercase", textAlign: "center", marginBottom: 18 },

  header: { fontFamily: FONT_DISPLAY, fontSize: 44, fontWeight: "700", color: "#f5f1e3", letterSpacing: 0.5, textAlign: "center" },
  tagline: { fontFamily: FONT_LABEL, fontSize: 18, fontWeight: "600", color: GOLD, textAlign: "center", marginTop: 6, marginBottom: 18 },

  intro: { fontSize: 15.5, lineHeight: 23, color: "#cdd8f2", textAlign: "center", maxWidth: 700, marginBottom: 12 },
  bullets: { alignSelf: "center", maxWidth: 680, marginBottom: 26 },
  bulletRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 7 },
  bulletDot: { color: GOLD, fontSize: 16, lineHeight: 22, marginRight: 8 },
  bulletText: { flex: 1, fontSize: 14.5, lineHeight: 22, color: "#9fb0d8" },

  tiles: { flexDirection: "row", flexWrap: "wrap", justifyContent: "center", gap: 14, marginBottom: 26 },
  tile: {
    width: 200,
    minHeight: 104,
    borderRadius: 16,
    paddingVertical: 18,
    paddingHorizontal: 16,
    backgroundColor: "rgba(15,33,77,0.55)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    justifyContent: "center",
  },
  tileOpen: { borderColor: GOLD, backgroundColor: "rgba(231,196,106,0.10)" },
  tileDone: { borderColor: "rgba(74,222,128,0.55)", backgroundColor: "rgba(34,197,94,0.10)" },
  tileLocked: { opacity: 0.5 },
  tileKicker: { fontFamily: FONT_LABEL, fontSize: 11, letterSpacing: 1.5, fontWeight: "700", color: GOLD, textTransform: "uppercase", marginBottom: 6 },
  tileTitle: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: "700", color: "#f5f1e3", lineHeight: 26 },

  qrBtn: {
    backgroundColor: GOLD,
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 14,
    marginTop: 4,
    shadowColor: GOLD,
    shadowOpacity: 0.4,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  qrBtnDisabled: { backgroundColor: "rgba(231,196,106,0.35)", shadowOpacity: 0 },
  qrBtnText: { color: "#0b2a63", fontWeight: "800", fontSize: 17, letterSpacing: 0.4, fontFamily: FONT_LABEL, textAlign: "center" },

  footer: { fontSize: 13, lineHeight: 20, color: "#7e8fc0", textAlign: "center", marginTop: 18, maxWidth: 640 },
});
